import { useCallback, useSyncExternalStore } from "react";

/**
 * Эмодзи: набор, поиск и память о выборе.
 *
 * Набор собирается заранее командой `npm run emoji:build` и лежит рядом
 * готовым файлом. Сюда он попадает по требованию — 274 КБ незачем тащить в
 * первый экран ради панели, которую откроют не в каждом сеансе.
 *
 * Разбор знаков в тексте живёт здесь же, но набора не требует и работает
 * сразу: лента рисуется раньше, чем кто-то нажмёт на кнопку панели, и ждать
 * загрузки ради размера знака нельзя.
 */

/** Раздел набора: вкладка в верхнем ряду панели. */
export interface EmojiSection {
  key: string;
  label: string;
  /** Знак на самой вкладке. */
  icon: string;
}

/**
 * Знак набора.
 *
 * В файле лежит массивом, а не объектом: полей пять, знаков почти две
 * тысячи, и имена полей весили бы больше самих данных.
 */
export type EmojiRow = [
  unicode: string,
  label: string,
  shortcodes: string[],
  section: number,
  tags: string[],
  tones?: string[],
];

export interface EmojiSet {
  sections: EmojiSection[];
  emoji: EmojiRow[];
}

/** Разобранный знак — то, чем пользуются панель и подсказка. */
export interface Emoji {
  /** Знак с уже применённым тоном кожи. */
  char: string;
  /** Знак без тона: по нему он и запоминается в недавних. */
  base: string;
  label: string;
  /** Первый шорткод — тот, что показывается в подвале панели. */
  code: string;
  section: number;
  /** Есть ли у знака тона кожи. */
  toned: boolean;
}

/**
 * Кастомный знак сервера.
 *
 * Тот же вид, что приходит с сервера. Панель и показ сообщений работают с
 * ними наравне с обычными, поэтому тип живёт здесь, а не в разметке.
 */
export interface CustomEmoji {
  id: string;
  /** Имя без двоеточий: в тексте знак записан как `:имя:`. */
  name: string;
  /** Файл картинки — забирается тем же путём, что аватары. */
  file_id: string;
  server_id: string;
  /** Имя сервера — нужно, когда знак пришёл с чужого. */
  server_name: string;
}

let pending: Promise<EmojiSet> | null = null;

/**
 * Уже загруженный набор, если он есть.
 *
 * Нужен показу сообщений: подставить имя знака во всплывающую подсказку
 * можно только тем, что уже под рукой, — ради подписи набор не грузят.
 */
let ready: EmojiSet | null = null;

/**
 * Набор целиком. Второй вызов ждёт первый, а не грузит заново.
 */
export function loadEmoji(): Promise<EmojiSet> {
  pending ??= import("./emoji-set.json").then((module) => {
    ready = module.default as EmojiSet;
    for (const watcher of setWatchers) watcher();
    return ready;
  });

  return pending;
}

/*
 * Кто ждёт набор.
 *
 * Тем же способом, что и недавние ниже: хранить состояние загрузки в каждом,
 * кому набор нужен, значило бы завести по копии на панель знаков и на поле
 * ввода — с эффектом, флагом «ещё жив» и лишней отрисовкой в каждой.
 */
const setWatchers = new Set<() => void>();

/**
 * Набор для показа: `null`, пока не приехал.
 *
 * Просить его отсюда можно из любого места и сколько угодно раз: загрузка одна
 * на приложение, а подписка отдаёт один и тот же объект, пока он не сменился.
 */
export function useEmojiSet(want = true): EmojiSet | null {
  return useSyncExternalStore(
    useCallback(
      (watcher: () => void) => {
        /*
         * Загрузка просится отсюда, а не из эффекта и не при отрисовке.
         *
         * React зовёт подписку после кадра — то есть ровно тогда, когда её
         * позвал бы эффект, — а отрисовка остаётся чистой. Повторные вызовы
         * ничего не стоят: `loadEmoji` отдаёт одно и то же обещание.
         *
         * `want` нужен полю ввода: четверть мегабайта приезжает, только когда
         * человек начал набирать знак, а не при каждом открытии канала.
         */
        if (want) void loadEmoji();

        setWatchers.add(watcher);
        return () => void setWatchers.delete(watcher);
      },
      [want],
    ),
    () => ready,
    () => null,
  );
}

// ── тон кожи ─────────────────────────────────────────────────────────────

const TONE_KEY = "airel.emoji.tone";

/**
 * Выбранный тон кожи: 0 — как в наборе, 1–5 — от самого светлого.
 *
 * Запоминается навсегда и на всё: спрашивать тон у каждого знака отдельно —
 * значит спрашивать его бесконечно.
 */
export function skinTone(): number {
  const raw = Number.parseInt(localStorage.getItem(TONE_KEY) ?? "", 10);
  return Number.isInteger(raw) && raw >= 0 && raw <= 5 ? raw : 0;
}

export function setSkinTone(tone: number) {
  localStorage.setItem(TONE_KEY, String(tone));
}

/** Пять тонов на выбор — их и показывает подвал панели. */
export const TONE_SAMPLES = ["✋", "✋🏻", "✋🏼", "✋🏽", "✋🏾", "✋🏿"];

// ── недавние ─────────────────────────────────────────────────────────────

const RECENT_KEY = "airel.emoji.recent";

/**
 * Сколько знаков помним.
 *
 * Три ряда по восемь: столько же, сколько влезает в блок панели, не заставляя
 * её прокручиваться ради недавнего. Больше — и «недавнее» перестаёт быть
 * недавним.
 */
const RECENT_LIMIT = 24;

/**
 * Недавние — в порядке от свежего.
 *
 * Хранятся строками, а не ссылками на набор: среди них бывают кастомные
 * знаки вида `:горит:`, которых в наборе нет вовсе, и знаки с тоном.
 */
export function recentEmoji(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as unknown;
    return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === "string") : [];
  } catch {
    // Испорченную запись молча заменяем пустой: недавние — не те данные,
    // ради которых стоит показывать человеку ошибку.
    return [];
  }
}

/** Запоминает выбор. Повтор поднимается наверх, а не задваивается. */
export function rememberEmoji(char: string) {
  const next = [char, ...recentEmoji().filter((item) => item !== char)].slice(0, RECENT_LIMIT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));

  /*
   * Счёт растёт сразу, а ряд быстрых реакций — нет: он стоит под курсором,
   * и переставлять его в тот самый миг, когда по нему попали, нельзя. Новый
   * порядок покажется, когда курсор уйдёт (`settleQuick`).
   */
  uses.set(char, (uses.get(char) ?? 0) + 1);
  dirty = true;

  for (const watcher of watchers) watcher();
}

/*
 * Кто смотрит на ряд быстрых реакций.
 *
 * Ряд висит в каждой строке ленты, и меняться он обязан во всех сразу, а не
 * в той одной, где нажали. Хранилище само об изменениях не сообщает —
 * сообщаем мы.
 */
const watchers = new Set<() => void>();

export function watchRecent(watcher: () => void): () => void {
  watchers.add(watcher);
  return () => void watchers.delete(watcher);
}

// ── ряд быстрых реакций ──────────────────────────────────────────────────

/**
 * Чем ряд заполняется, пока про человека ничего не известно.
 *
 * Тот же набор, что был когда-то жёстко зашит в строке сообщения. Он же
 * добирает ряд до пяти, когда своих знаков набралось меньше.
 */
const FALLBACK_QUICK = ["✅", "🔥", "👀", "🙏", "😅"];

/** Сколько знаков в ряду. */
const QUICK_SIZE = 5;

/**
 * Сколько раз человек ставил каждый знак.
 *
 * Приходит с сервера — он считает по самим реакциям, поэтому счёт переживает
 * и перезаход, и смену устройства, — и растёт здесь же на всё, что выбрано
 * за этот сеанс.
 */
const uses = new Map<string, number>();

/** Порядок изменился, но показывать новый ещё рано. */
let dirty = false;

/*
 * Готовый ряд держится собранным до `settleQuick`.
 *
 * Не ради скорости: на него подписаны строки ленты, а подписка требует,
 * чтобы неизменившийся ответ был тем же самым массивом. Собирай мы его
 * заново на каждый вызов, показ считал бы данные изменившимися всегда и
 * перерисовывался бы без конца.
 */
let quick: string[] = FALLBACK_QUICK;

/**
 * Ряд по частоте, а не по свежести.
 *
 * Свежесть — величина быстрая: она меняется от одного нажатия, и знак,
 * которым только что ответили, прыгает на первое место. Ряд же бьют не
 * глядя — «второй слева» рука запоминает за десяток нажатий, — и мишень,
 * которая двигается от попадания, наказывает за точность.
 *
 * Частота меняется медленно: чтобы знак вошёл в пятёрку, его надо выбрать не
 * раз и не два. Свежесть при этом не пропала — она осталась там, где вреда
 * от неё нет: в блоке «недавние» внутри панели.
 */
function order(): string[] {
  const previous = new Map(quick.map((char, at) => [char, at]));

  const known = [...uses.entries()]
    .filter(([, count]) => count > 0)
    .sort(
      (a, b) =>
        b[1] - a[1] ||
        // При равном счёте держимся за прежние места: перестановка ради
        // ничьей — то же самое дёрганье, только реже.
        (previous.get(a[0]) ?? Infinity) - (previous.get(b[0]) ?? Infinity) ||
        a[0].localeCompare(b[0]),
    )
    .map(([char]) => char);

  const filled = [...known, ...FALLBACK_QUICK.filter((item) => !known.includes(item))];
  return filled.slice(0, QUICK_SIZE);
}

/** Складывает новый ряд, если он и правда другой. */
function restack() {
  const next = order();
  dirty = false;

  if (next.length === quick.length && next.every((char, at) => char === quick[at])) return;

  quick = next;
  for (const watcher of watchers) watcher();
}

export function quickEmoji(): string[] {
  return quick;
}

/**
 * Статистика с сервера: чем человек чаще всего отвечает.
 *
 * Зовётся раз за сеанс, при запуске, — там ряда ни у кого на экране нет, и
 * переставить его можно сразу.
 */
export function seedQuick(items: { emoji: string; uses: number }[]) {
  for (const item of items) {
    // Своё, набранное за сеанс, не затираем: оно новее присланного.
    uses.set(item.emoji, Math.max(item.uses, uses.get(item.emoji) ?? 0));
  }

  restack();
}

/**
 * Курсор ушёл со строки — можно переставлять.
 *
 * Ряд показывается по наведению, и это единственное окно, когда его порядок
 * менять безопасно: смотреть на него в этот миг некому.
 */
export function settleQuick() {
  if (dirty) restack();
}

/** Забыть набранное за сеанс. Зовётся при выходе, как и сторы. */
export function forgetQuick() {
  uses.clear();
  dirty = false;
  quick = FALLBACK_QUICK;
}

// ── разбор набора ────────────────────────────────────────────────────────

/** Знак с учётом выбранного тона. */
export function withTone(row: EmojiRow, tone: number): string {
  const tones = row[5];
  if (!tones || tone === 0) return row[0];
  return tones[tone - 1] ?? row[0];
}

export function toEmoji(row: EmojiRow, tone: number): Emoji {
  return {
    char: withTone(row, tone),
    base: row[0],
    label: row[1],
    code: row[2][0],
    section: row[3],
    toned: Boolean(row[5]),
  };
}

/**
 * Поиск по названию, синонимам и шорткоду разом.
 *
 * Одним запросом по обеим сторонам: человек набирает то слово, которое
 * помнит, и заранее не знает, русское оно или английское. «гор» находит
 * гору, «fire» — огонь, и спрашивать, на каком языке он думает, незачем.
 *
 * Начало названия идёт выше начала слова внутри него, а то и другое — выше
 * совпадения в середине: по «сер» ждут сердце, а не фломастер.
 */
export function searchEmoji(set: EmojiSet, query: string, limit = 48): EmojiRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  const hits: { row: EmojiRow; rank: number }[] = [];

  for (const row of set.emoji) {
    const rank = rankOf(row, needle);
    if (rank !== null) hits.push({ row, rank });
  }

  hits.sort((left, right) => left.rank - right.rank);
  return hits.slice(0, limit).map((hit) => hit.row);
}

function rankOf(row: EmojiRow, needle: string): number | null {
  const label = row[1].toLowerCase();
  const codes = row[2].map((code) => code.toLowerCase());
  const tags = row[4].map((tag) => tag.toLowerCase());

  if (label === needle || codes.includes(needle)) return 0;
  if (label.startsWith(needle)) return 1;
  if (codes.some((code) => code.startsWith(needle))) return 2;

  if (startsWord(label, needle)) return 3;
  if (codes.some((code) => startsWord(code.replace(/_/g, " "), needle))) return 4;
  if (tags.some((tag) => startsWord(tag, needle))) return 5;

  if (label.includes(needle)) return 6;
  if (tags.some((tag) => tag.includes(needle))) return 7;
  if (codes.some((code) => code.includes(needle))) return 8;

  return null;
}

/** Начинается ли с иглы какое-нибудь слово внутри названия. */
function startsWord(haystack: string, needle: string): boolean {
  return haystack.split(/[\s,_-]+/).some((word) => word.startsWith(needle));
}

/** Кастомные знаки, подходящие под запрос. Ищутся только по имени. */
export function searchCustom(list: CustomEmoji[], query: string): CustomEmoji[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  return list
    .filter((item) => item.name.toLowerCase().includes(needle))
    .sort((left, right) => {
      const l = left.name.toLowerCase().startsWith(needle) ? 0 : 1;
      const r = right.name.toLowerCase().startsWith(needle) ? 0 : 1;
      return l - r || left.name.localeCompare(right.name);
    });
}

/**
 * Название знака для всплывающей подсказки.
 *
 * Пока набор не загружен, возвращает сам знак: подсказка «🔥» честнее
 * пустой, и грузить ради неё четверть мегабайта незачем.
 */
export function labelOf(char: string): string {
  if (!ready) return char;

  const row = ready.emoji.find((item) => item[0] === char || item[5]?.includes(char));
  return row ? row[1] : char;
}

// ── знаки в тексте ───────────────────────────────────────────────────────

/**
 * Один знак: картинка с модификатором тона и цепочки через нулевой пробел.
 *
 * Взяты только те знаки, что рисуются картинкой по умолчанию
 * (`Emoji_Presentation`), и те, у кого об этом прямо просит вариационный
 * селектор. Иначе под правило попали бы «©», «™» и стрелки: они тоже
 * пиктограммы по классификации Unicode, но в тексте это знаки препинания, и
 * раздувать их до восемнадцати пунктов — портить абзац.
 *
 * Флаги идут отдельной веткой: это пара региональных букв, ни одна из
 * которых сама по себе не пиктограмма. Клавиши вроде «1️⃣» — тоже.
 */
const UNIT =
  "(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F)\\p{Emoji_Modifier}?";

/*
 * Флаг стоит первым не для красоты: региональная буква сама рисуется
 * картинкой и подходит под общее правило. Проверяй мы её после, «🇷🇺»
 * разваливалось бы на две половины флага — по знаку на букву.
 */
export const EMOJI_PATTERN = new RegExp(
  `(?:\\p{Regional_Indicator}{2}|[0-9#*]\\uFE0F?\\u20E3|${UNIT}(?:\\u200D${UNIT})*)`,
  "gu",
);

/** Кусок текста: знак или всё остальное. */
export type TextRun = { emoji: false; text: string } | { emoji: true; text: string };

/**
 * Режет строку на знаки и текст между ними.
 *
 * Нужно показу: знак в строке набирается своим кеглем, и обернуть его надо
 * ровно по границе, не задев соседние буквы.
 */
export function splitEmoji(text: string): TextRun[] {
  const runs: TextRun[] = [];
  let at = 0;

  // Регулярное выражение общее и хранит положение между вызовами — сбрасываем.
  EMOJI_PATTERN.lastIndex = 0;

  for (const match of text.matchAll(EMOJI_PATTERN)) {
    const start = match.index;
    if (start > at) runs.push({ emoji: false, text: text.slice(at, start) });
    runs.push({ emoji: true, text: match[0] });
    at = start + match[0].length;
  }

  if (at < text.length) runs.push({ emoji: false, text: text.slice(at) });

  return runs;
}

/** Есть ли в строке хоть один знак. */
export function hasEmoji(text: string): boolean {
  EMOJI_PATTERN.lastIndex = 0;
  return EMOJI_PATTERN.test(text);
}

/**
 * Сколько знаков в сообщении, если в нём нет ничего кроме них.
 *
 * `0` — есть что-то ещё, и правило крупного кегля не применяется. Пробелы
 * между знаками не считаются содержимым: «🙏 🔥» — тот же жест, что «🙏🔥».
 *
 * Считается по исходному тексту, а не по разобранной разметке: жирный или
 * ссылка в сообщении сразу означают, что оно не «только знаки», и разбирать
 * его ради этого незачем.
 */
export function soleEmojiCount(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;

  let count = 0;

  for (const run of splitEmoji(trimmed)) {
    if (run.emoji) {
      count += 1;
      continue;
    }

    // Между знаками допустимы только пробелы.
    if (run.text.trim()) return 0;
  }

  return count;
}

/**
 * Правило кегля из макета: один–три знака и ничего больше — крупно.
 *
 * С четвёртого отключается: шесть знаков по 34 пункта — уже не жест, а
 * плакат посреди разговора.
 */
export const JUMBO_LIMIT = 3;

export function isJumbo(text: string): boolean {
  const count = soleEmojiCount(text);
  return count > 0 && count <= JUMBO_LIMIT;
}

